import React from "react";
import { Box, IconButton, Typography, TextField, Grid } from "@mui/material";
import {
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import {
  fonts,
  headingFonts,
  textFieldStyles,
} from "../../../../Styles/Styles";

const ImageUpload = ({ images, setImages }) => {
  // Handle image preview selection
  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const totalFiles = images.length + selectedFiles.length;

    if (totalFiles > 10) {
      alert("You can only upload up to 10 images. Please select images again.");
      e.target.value = ""; // Clear input field
      setImages([]); // Clear selected images
      return;
    }

    const imageFiles = selectedFiles.map((file) => {
      const sizeMB = file.size / 1024 / 1024;
      return {
        id: URL.createObjectURL(file),
        file,
        name: file.name,
        size: sizeMB.toFixed(2),
        isOversized: sizeMB > 15,
      };
    });
    setImages((prevImages) => [...prevImages, ...imageFiles]);
  };

  // Remove image from the list
  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  // Move image up in the list
  const moveUp = (index) => {
    if (index === 0) return; // Already at the top
    const newImages = [...images];
    const [movedImage] = newImages.splice(index, 1);
    newImages.splice(index - 1, 0, movedImage);
    setImages(newImages);
  };

  // Move image down in the list
  const moveDown = (index) => {
    if (index === images.length - 1) return; // Already at the bottom
    const newImages = [...images];
    const [movedImage] = newImages.splice(index, 1);
    newImages.splice(index + 1, 0, movedImage);
    setImages(newImages);
  };

  return (
    <Box>
      <TextField
        label="Images"
        type="file"
        inputProps={{ multiple: true, accept: "image/*" }}
        variant="outlined"
        fullWidth
        onChange={handleImageChange}
        required
        margin="normal"
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ ...textFieldStyles }}
      />
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ ...fonts, mt: 1, mb: 1, textAlign: "left" }}
      >
        <b>No. of Images allowed: </b>10
        <br />
        <b>Max size:</b> 15MB each
      </Typography>
      {images.length > 0 && (
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <ImagePreview
                image={image}
                index={index}
                removeImage={removeImage}
                moveUp={moveUp}
                moveDown={moveDown}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

const ImagePreview = ({ image, index, removeImage, moveUp, moveDown }) => {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        padding: 1,
        border: image.isOversized ? "2px solid red" : "1px solid #ddd",
        backgroundColor: image.isOversized ? "#fff6f6" : "inherit",
        borderRadius: 2,
        width: "100%",
        height: "100%",
      }}
    >
      {/* Display numbering at the top left of each preview */}
      <Box
        sx={{
          fontFamily: "ClashGrotesk-Medium",
          letterSpacing: 1,
          position: "absolute",
          top: 0,
          backgroundColor: "var(--secondary-color)",
          color: "white",
          padding: "2px 6px",
          borderRadius: "0 0 4px 0",
          fontSize: "0.8rem",
          fontWeight: "bold",
        }}
      >
        {index + 1}
      </Box>
      <Box
        component="img"
        src={image.id}
        alt={image.name}
        sx={{
          width: 80,
          height: 80,
          objectFit: "cover",
          marginRight: 2,
        }}
      />
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Typography
          sx={{
            flexGrow: 1,
            fontSize: 14,
            ...headingFonts,
            wordBreak: "break-word", // Ensures long words wrap
            overflowWrap: "break-word", // Alternative way to wrap
            whiteSpace: "normal", // Allows text to wrap normally
          }}
        >
          {image.name}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ ...fonts }}>
          {image.size} MB
        </Typography>
        {image.isOversized && (
          <Typography
            variant="caption"
            color="error"
            sx={{ ...headingFonts, fontWeight: "Semibold" }}
          >
            Exceeds 15MB limit
          </Typography>
        )}
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <IconButton onClick={() => moveUp(index)} sx={{ marginBottom: 0.5 }}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => moveDown(index)} sx={{ marginBottom: 0.5 }}>
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => removeImage(index)} sx={{ color: "red" }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ImageUpload;
